import { create } from 'zustand'

import { FeatureFlagKeys } from '@hapana/shared/constants/feature-flag.constants'
import { PendingItemsErrorCode } from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'

import useSiteStore from './site.store'

type RefusalExtensionsLike = { code?: unknown; details?: { feature?: unknown } | null } | null

type GraphqlErrorLike = { extensions?: RefusalExtensionsLike }

type RequestErrorLike = {
  code?: unknown
  details?: { feature?: unknown } | null
  extensions?: RefusalExtensionsLike
  response?: { errors?: GraphqlErrorLike[] | null } | null
}

// Other gated features refuse with the same code, so only a refusal that names the policy
// compliance flag counts; a questionnaire or device refusal must never switch this gate off.
const isPolicyComplianceRefusal = (
  code: unknown,
  details: { feature?: unknown } | null | undefined
): boolean =>
  code === PendingItemsErrorCode.FEATURE_UNAVAILABLE &&
  details?.feature === FeatureFlagKeys.PolicyComplianceGate

const NO_SITE_KEY = ''

type PolicyComplianceAvailabilityStore = {
  refusedSiteIds: ReadonlySet<string>
  markRefusedForSite: (siteId: string | null) => void
}

export const usePolicyComplianceAvailabilityStore = create<PolicyComplianceAvailabilityStore>(
  (set) => ({
    refusedSiteIds: new Set<string>(),
    markRefusedForSite: (siteId) =>
      set((state) => {
        const key = siteId ?? NO_SITE_KEY
        if (state.refusedSiteIds.has(key)) {
          return state
        }
        return { refusedSiteIds: new Set([...state.refusedSiteIds, key]) }
      }),
  })
)

export const isPolicyComplianceUnavailableError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const requestError = error as RequestErrorLike
  return (
    isPolicyComplianceRefusal(requestError.code, requestError.details) ||
    isPolicyComplianceRefusal(requestError.extensions?.code, requestError.extensions?.details) ||
    (requestError.response?.errors ?? []).some((graphqlError) =>
      isPolicyComplianceRefusal(graphqlError?.extensions?.code, graphqlError?.extensions?.details)
    )
  )
}

export const recordPolicyComplianceRefusal = (error: unknown): void => {
  if (!isPolicyComplianceUnavailableError(error)) {
    return
  }
  usePolicyComplianceAvailabilityStore
    .getState()
    .markRefusedForSite(useSiteStore.getState().getCurrentSiteId())
}

export const useIsPolicyComplianceRefusedByServer = (): boolean => {
  const siteId = useSiteStore(
    (state: { currentSite: { siteId?: string } | null }) => state.currentSite?.siteId ?? null
  )
  return usePolicyComplianceAvailabilityStore((state) =>
    state.refusedSiteIds.has(siteId ?? NO_SITE_KEY)
  )
}
